import { Component, OnInit, inject, DestroyRef, effect } from '@angular/core';
import { Validators, FormBuilder, ValidatorFn, AbstractControl, FormGroup, FormArray, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IEntityGroup } from '@app/core/services/custom-services/entity-group.service';
import { EntityPropertyService } from '@app/core/services/http-services/model/entity-property.service';
import { EntityRelationDirection, EntityService, IEntity, IEntityRelation, IGlulamSpec, IPropertyValue } from '@app/core/services/http-services/model/entity.service';
import { UnitService } from '@app/core/services/http-services/common/unit-service';
import { AppNotificationService } from '@app/core/services/custom-services/notification.service';
import { _, TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CommonHelper } from '@app/shared/helpers/common-helper';
import { ProdOrderService } from '@app/core/services/http-services/operative/prod-order.service';
import { BatchTemplateService } from '@app/core/services/http-services/operative/batch-template.service';
import { MachineGroupService } from '@app/core/services/http-services/operative/machine-group.service';
import { FileDownloadMode } from '@app/core/models/file-download-mode-enum';
import { IUnit } from '@app/core/models/unit.model';
import { IUnitConversion } from '@app/core/models/unitConversion.model';
import { EntityPropertyDataTypeEnum } from '@app/core/models/entity-property-data-type.enum';
import { DialogRef, DialogContentBase, DialogCloseResult, KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import { BeamLamminaDimension } from '@app/core/models/beam-lammina-dimension.model';
import { DimensionService } from '@app/core/services/http-services/gluelam/dimension.service';
import { CreateGlulamSpecRowForm } from '@app/core/models/forms/gluelam/glulam-spec/create-glulam-spec-row-form.model';
import { SVGIcon, minusIcon, plusIcon } from '@progress/kendo-svg-icons';
import { Guid } from '@app/core/models/Guid';
import { KENDO_FORM, KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { KENDO_LABELS } from '@progress/kendo-angular-label';
import { SelectUnitComponent } from '@app-module//app-controls/select-unit/select-unit.component';
import { SelectEntityStatusComponent } from '@app-module/app-controls/select-entity-status/select-entity-status.component';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { SelectValidValueComponent } from '@app-module/app-controls/select-valid-value/select-valid-value.component';
import { KENDO_BUTTONS } from '@progress/kendo-angular-buttons';
import { SelectEntityComponent } from '@app-module/app-controls/select-entity/select-entity.component';
import { SelectEntityRelationDirectionComponent } from '@app-module/app-controls/select-entity-relation-direction/select-entity-relation-direction.component';
import { ConfirmationDialogComponent } from '@app/core/components/confirmation-dialog/confirmation-dialog.component';
import { CreateEntityForm } from '@app/core/models/forms/entity-admin/entity-form/create-entity-form.model';
import { CreatePropertyValueEntityForm } from '@app/core/models/forms/entity-admin/entity-form/create-entity-property-value-form.model';
import { CreateEntityRelationForm } from '@app/core/models/forms/entity-admin/entity-form/create-entity-relation-form.model';
import { CreateEntityUnitConversionForm } from '@app/core/models/forms/entity-admin/entity-form/create-entity-unit-conversion-form.model';
import { CreateGlulamSpecForm } from '@app/core/models/forms/gluelam/glulam-spec/create-glulam-spec-form.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { GroupResult, groupBy, State } from '@progress/kendo-data-query';
import { KENDO_GRID } from '@progress/kendo-angular-grid';
import { IEntityProperty } from '@app/core/models/entity-property.model';
import { MachineSelectorComponent } from '@app/features/app-controls/machine-selector/machine-selector.component';
import { SelectBatchTemplateComponent } from '@app/features/app-controls/select-batch-template/select-batch-template.component';
import { FileUploadComponent } from '@app/features/app-controls/file-upload/file-upload.component';

_([
  'EntityTranslation.EntityRelationInvalidRelationForConsume',
  'EntityTranslation.InstructionFileDeleted',
  'EntityTranslation.EntityCodeLengthError',
  'EntityTranslation.InvalidDataType',
  'EntityTranslation.Activity',
  'EntityTranslation.Material',
  'EntityTranslation.ConfirmEntityRelationRemoval',
  'EntityTranslation.SortOrder',
  'EntityTranslation.RelationActivity',
  'Abbreviations.Minute'
]);

@Component({
  selector: 'app-activity-form',
  templateUrl: './activity-form.component.html',
  styleUrls: ['./activity-form.component.css'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateDirective,
    SelectUnitComponent,
    SelectEntityStatusComponent,
    SelectValidValueComponent,
    SelectEntityComponent,
    SelectEntityRelationDirectionComponent,
    TranslatePipe,
    ConfirmationDialogComponent,
    MachineSelectorComponent,
    SelectBatchTemplateComponent,
    FileUploadComponent,
    KENDO_GRID,
    KENDO_INPUTS,
    KENDO_FORM,
    KENDO_DIALOG,
    KENDO_DROPDOWNS,
    KENDO_LABELS,
    KENDO_BUTTONS,
  ],
})
export class ActivityFormComponent extends DialogContentBase implements OnInit {
  _entity: IEntity;
  _entityGroup: IEntityGroup;
  _entityRelation: IEntityRelation;
  units: IUnit[];
  entityRelationForm: FormGroup;
  disableSubmit: boolean = false;
  fileDownloadMode = FileDownloadMode;
  entityForm!: FormGroup<CreateEntityForm>;
  isNew: boolean;
  isGlulamSpecEnabled = false;
  confirmRemoveRelationIndex: number | null = null;
  loading: boolean = false;

  minusIcon: SVGIcon = minusIcon;
  plusIcon: SVGIcon = plusIcon;

  translationService = inject(TranslateService);

  dimensions: BeamLamminaDimension[] = [];
  batchTemplateOptions: GroupResult[] = [];
  noDimensionItem = { beamLamminaDimensionID: null, description: this.translationService.instant('App.SelectItem') };
  private glulamSpecForm!: FormGroup<CreateGlulamSpecForm>;

  get entityRelations(): FormArray<FormGroup<CreateEntityRelationForm>> {
    return this.entityForm.controls.entityRelations;
  }

  get entityRelationGridData(): { relationGroup: AbstractControl; formIndex: number; sortOrder: number }[] {
    return this.entityRelations.controls
      .map((relationGroup, formIndex) => ({
        relationGroup,
        formIndex,
        sortOrder: relationGroup.controls.sortOrder?.value ?? formIndex + 1,
      }))
      .filter(item => item.relationGroup.value?.isRemoved === false);
  }

  glulamSpecs(): FormArray {
    return this.glulamSpecForm.controls.specrows;
  }

  newEntityRelation(): FormGroup {
    return this.createEntityRelationFormGroup(
      <IEntityRelation>{
        sortOrder: this.getNextRelationSortOrder(),
        relation: null,
        direction: EntityRelationDirection.CONSUMES,
        unitCode: 'M3',
        userCode: null,
        inTactical: true,
        inOperative: true,
        isChaining: false,
        batchTemplateId: null,
        isPreferred: false,
      },
      0,
      true
    );
  }

  addNewEntityRelation() {
    this.entityRelations.push(this.newEntityRelation());
    this.recalculateEntityRelationSortOrder();
  }

  onEntityRelationEntitySelected(entity: IEntity, index: number) {
    const row = this.entityRelations.at(index);
    if (entity?.baseUnitCode && (row.value.isNew || !row.value.unitCode)) {
      row.controls.unitCode.patchValue(entity.baseUnitCode);
    }
  }

  removeEntityRelation(entityIndex: number) {
    this.confirmRemoveRelationIndex = entityIndex;
  }

  confirmEntityRelationRemoval(isConfirmed: boolean) {
    if (isConfirmed && this.confirmRemoveRelationIndex !== null) {
      const index = this.confirmRemoveRelationIndex;
      this.entityRelations.at(index).controls.isRemoved.patchValue(true);
      if (this.entityRelations.at(index).value.isNew) {
        this.entityRelations.removeAt(index);
      }
      this.recalculateEntityRelationSortOrder();
    }
    this.confirmRemoveRelationIndex = null;
  }

  buildForm() {
    this.entityForm = this.formBuilder.group<CreateEntityForm>({
      index: this.formBuilder.control(this._entity.index, [Validators.required]),
      code: this.formBuilder.control(this._entity.code, [Validators.required, Validators.maxLength(100)]),
      baseUnitCode: this.formBuilder.control(this._entity.baseUnitCode ?? '', [Validators.required, Validators.maxLength(5)]),
      changeDate: this.formBuilder.control(this._entity.changeDate),
      commentId: this.formBuilder.control(this._entity.commentId),
      description: this.formBuilder.control(this._entity.description, [Validators.required]),
      entityGroupCode: this.formBuilder.control(this._entity.entityGroupCode),
      isDynamicActivity: this.formBuilder.control(this._entity.isDynamicActivity),
      sortOrderIndex: this.formBuilder.control(this._entity.sortOrderIndex),
      status: this.formBuilder.control(this._entity.status, [Validators.required]),
      superiorEntityIndex: this.formBuilder.control(this._entity.superiorEntityIndex),
      userCode: this.formBuilder.control(this._entity.userCode),
      machineId: this.formBuilder.control(this._entity.machineId),
      instruction: this.formBuilder.control(this._entity.instruction),
      propertyValues: this.formBuilder.array(this._entity.propertyValues.map(pv => this.createPropertyValueFormGroup(pv))),
      entityRelations: this.formBuilder.array(
        this._entity.entityRelations.map(enr => this.createEntityRelationFormGroup(enr, enr.productIndex, false)) ?? []),
      unitConversions: this.formBuilder.array(this._entity.unitConversions.map(uc => this.createUnitConversionFormGroup(uc, false))),
      targetMinStock: this.formBuilder.control(this._entity.targetMinStock),
      targetMaxStock: this.formBuilder.control(this._entity.targetMaxStock),
      batchTime: this.formBuilder.control(this._entity.batchTime, [Validators.max(32767), Validators.min(0)]),
      batchSize: this.formBuilder.control(this._entity.batchSize, [Validators.min(0), Validators.max(999_999.999)]),
      guid: this.formBuilder.control(this._entity.guid),
      beamLamminaDimensionId: this.formBuilder.control(this._entity.beamLamminaDimensionId),
      autoGenerateProdOrder: this.formBuilder.control(this._entity.autoGenerateProdOrder),
    });
    this.recalculateEntityRelationSortOrder();
    this.buildFormGlue();
  }

  createPropertyValueFormGroup(pv: IPropertyValue): FormGroup<CreatePropertyValueEntityForm> {
    return this.formBuilder.group<CreatePropertyValueEntityForm>({
      id: this.formBuilder.control(pv.id),
      entityIndex: this.formBuilder.control(pv.entityIndex),
      code: this.formBuilder.control(pv.propertyCode),
      value: this.formBuilder.control(this.getStringValue(pv)),
      dataType: this.formBuilder.control(pv.dataType),
      isMandatory: this.formBuilder.control(pv.isMandatory),
      areValuesValidated: this.formBuilder.control(pv.areValuesValidated),
      description: this.formBuilder.control(pv.description)
    });
  }

  createEntityRelationFormGroup(enr: IEntityRelation, entityIndex: number, isNew: boolean): FormGroup<CreateEntityRelationForm> {
    const formGroup = this.formBuilder.group<CreateEntityRelationForm>({
      sortOrder: this.formBuilder.control(enr.sortOrder ?? 0),
      entityIndex: this.formBuilder.control({ value: entityIndex, disabled: !isNew }),
      relation: this.formBuilder.control(enr.relation, [this.validateRelationByUnitCode()]),
      direction: this.formBuilder.control(enr.direction, [this.validateDirectionForNegativeRelation()]),
      unitCode: this.formBuilder.control(enr.unitCode),
      userCode: this.formBuilder.control(enr.userCode),
      changeDate: this.formBuilder.control(enr.ChangeDate),
      productCode: this.formBuilder.control(enr.productCode),
      activityCode: this.formBuilder.control(enr.activityCode),
      productIndex: this.formBuilder.control(enr.productIndex),
      activityIndex: this.formBuilder.control(enr.activityIndex),
      isNew: this.formBuilder.control(isNew),
      isChange: this.formBuilder.control(false),
      isRemoved: this.formBuilder.control(false),
      inTactical: this.formBuilder.control(enr.inTactical),
      inOperative: this.formBuilder.control(enr.inOperative),
      isChaining: this.formBuilder.control(enr.isChaining),
      batchTemplateId: this.formBuilder.control(enr.batchTemplateId),
      isPreferred: this.formBuilder.control(enr.isPreferred),
    });
    return formGroup;
  }

  createUnitConversionFormGroup(uc: IUnitConversion, isNew: boolean): FormGroup<CreateEntityUnitConversionForm> {
    return this.formBuilder.group<CreateEntityUnitConversionForm>({
      index: this.formBuilder.control(uc.index),
      entityIndex: this.formBuilder.control(uc.entityIndex),
      unitCode: this.formBuilder.control(uc.unitCode),
      convFactor: this.formBuilder.control(uc.convFactor),
      userCode: this.formBuilder.control(uc.userCode),
      changeDate: this.formBuilder.control(uc.changeDate),
      isNew: this.formBuilder.control(isNew),
      isChange: this.formBuilder.control(false),
      isRemoved: this.formBuilder.control(false),
      id: this.formBuilder.control(uc.id),
      mandatoryUnitRelation: this.formBuilder.control(false),
    });
  }

  buildFormGlue() {
    const specrows = new FormArray([]);
    const relations = this._entity.entityRelations.filter(er => er.direction === EntityRelationDirection.CONSUMES && er.unitCode !== 'HOUR');

    relations.forEach(er => {
      er.glulamSpec.forEach(g => {
        specrows.push(this.newLine(g));
      });
    });

    this.glulamSpecForm = this.formBuilder.group<CreateGlulamSpecForm>({
      specrows: specrows,
      hightMin: this.formBuilder.control(this._entity.gluSpecHightMin, [Validators.min(0)]),
      hightMax: this.formBuilder.control(this._entity.gluSpecHightMax, [Validators.min(1)]),
      topPercentage: this.formBuilder.control(CommonHelper.fromPercentageToNumber(this._entity.gluSpecTopPercentage) ?? 0.18),
      bottomPercentage: this.formBuilder.control(CommonHelper.fromPercentageToNumber(this._entity.gluSpecBottomPercentage) ?? 0.18),
      addedCalcHight: this.formBuilder.control(this._entity.gluSpecAddedCalcHight ?? 0),
      addedCalcWidth: this.formBuilder.control(this._entity.gluSpecAddedCalcWidth ?? 0),
    });
  }

  public newLine(gs: IGlulamSpec): FormGroup<CreateGlulamSpecRowForm> {
    return this.formBuilder.group<CreateGlulamSpecRowForm>({
      activityIndex: this.formBuilder.control(gs.activityIndex),
      productIndex: this.formBuilder.control(gs.productIndex, [Validators.required]),
      sequence: this.formBuilder.control(gs.sequence),
      numberOfLammellas: this.formBuilder.control(gs.numberOfLammellas),
      planingThickness: this.formBuilder.control(gs.planingThickness, [Validators.required]),
      maxThickness: this.formBuilder.control(0),
      heightDependant: this.formBuilder.control(gs.heightDependant),
      dryJoint: this.formBuilder.control(gs.dryJoint),
      turn: this.formBuilder.control(gs.turn),
      isNew: this.formBuilder.control(gs.isNew),
      isChanged: this.formBuilder.control(gs.isChanged),
      isRemoved: this.formBuilder.control(gs.isRemoved),
      layerType: this.formBuilder.control(gs.layerType),
    });
  }

  private readonly unitService: UnitService = inject(UnitService);
  private readonly entityService: EntityService = inject(EntityService);
  private readonly poService: ProdOrderService = inject(ProdOrderService);
  private readonly entityPropertyService: EntityPropertyService = inject(EntityPropertyService);
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  private readonly appNotificationService: AppNotificationService = inject(AppNotificationService);
  private readonly dimensionService: DimensionService = inject(DimensionService);
  private readonly batchTemplateService: BatchTemplateService = inject(BatchTemplateService);
  private readonly machineGroupService: MachineGroupService = inject(MachineGroupService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  public data: { entity: IEntity; entityGroup: IEntityGroup; isNew: boolean; };

  private props: IEntityProperty[] = [];

  getProperty(code: string): IEntityProperty {
    const property = this.props.find(p => p.code === code);
    if (property) {
      return property;
    }

    const propertyValue = this._entity?.propertyValues?.find(pv => pv.propertyCode === code);
    return {
      code,
      description: propertyValue?.description ?? code,
      name: code,
      propertyGroupCode: '',
      dataType: propertyValue?.dataType ?? EntityPropertyDataTypeEnum.DATA_TYPE_STRING,
      areValuesValidated: propertyValue?.areValuesValidated ?? false,
      sortOrderIndex: 0,
      userCode: '',
      changeDate: null,
      entityGroupProperties: [],
      validValues: [],
    };
  }

  constructor(private readonly dialogRef: DialogRef) {
    super(dialogRef);
    this.entityRelationForm = this.formBuilder.group({
      entityRelations: this.formBuilder.array([]),
    });
    effect(() => {
      if (this.data?.entityGroup) {
        this.entityPropertyService.getEntityPropertysForGroup(this.data.entityGroup.index).subscribe(s => {
          this.props = s;
        });
      }
    });
  }

  ngOnInit() {
    this.unitService
      .query({})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(u => {
        this.units = u;
      });

    this.dimensionService
      .getBeamLamminaDimentions(true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(d => {
        this.dimensions = d;
      });

    if (this.data?.entity) {
      this.initializeWithEntity(this.data.entity, this.data.entityGroup);
    } else {
      this.loading = true;
    }
  }

  initializeWithEntity(entity: IEntity, entityGroup: IEntityGroup): void {
    this._entity = entity;
    this._entityGroup = entityGroup;
    this.isNew = this.data?.isNew ?? false;
    this.isGlulamSpecEnabled = entityGroup.isGlulamGroup;
    this.buildForm();
    this.loadBatchTemplates();
    this.loading = false;

    this.entityPropertyService.getEntityPropertysForGroup(entityGroup.index)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(s => { this.props = s; });

    this.entityRelations.controls.forEach(formGroup => {
      formGroup
        .get('unitCode')
        ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          formGroup.controls.relation?.updateValueAndValidity();
        });

      formGroup
        .get('relation')
        ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          formGroup.controls.direction?.updateValueAndValidity();
        });

      formGroup.controls.relation?.updateValueAndValidity({ emitEvent: false });
      formGroup.controls.direction?.updateValueAndValidity({ emitEvent: false });
    });
  }

  onSubmit() {
    const formValue = this.entityForm.getRawValue();
    const glulamSpecFormValue = this.glulamSpecForm?.value;

    const e: IEntity = <IEntity>{
      index: formValue.index ?? 0,
      code: formValue.code,
      baseUnitCode: formValue.baseUnitCode,
      changeDate: formValue.changeDate,
      commentId: formValue.commentId,
      description: formValue.description,
      entityGroup: this._entityGroup,
      entityGroupCode: formValue.entityGroupCode,
      sortOrderIndex: formValue.sortOrderIndex,
      status: formValue.status,
      userCode: formValue.userCode,
      machineId: formValue.machineId,
      targetMinStock: null,
      targetMaxStock: null,
      batchTime: formValue.batchTime ?? null,
      batchSize: formValue.batchSize ?? null,
      gluSpecHightMin: glulamSpecFormValue?.hightMin,
      gluSpecHightMax: glulamSpecFormValue?.hightMax,
      gluSpecAddedCalcHight: glulamSpecFormValue?.addedCalcHight,
      gluSpecAddedCalcWidth: glulamSpecFormValue?.addedCalcWidth,
      gluSpecBottomPercentage: CommonHelper.toPercentage(glulamSpecFormValue?.bottomPercentage),
      gluSpecTopPercentage: CommonHelper.toPercentage(glulamSpecFormValue?.topPercentage),
      guid: formValue.guid ?? Guid.newGuid(),
      beamLamminaDimensionId: formValue.beamLamminaDimensionId,
      instruction: formValue.instruction,
      propertyValues: formValue.propertyValues
        .filter(c => c.value != null && c.value !== '')
        .map(c => {
          return <IPropertyValue>{
            id: c.id,
            entityIndex: formValue.index,
            propertyCode: c.code,
            valueInt: c.dataType === EntityPropertyDataTypeEnum.DATA_TYPE_INT ? +c.value : 0,
            valueDouble: c.dataType === EntityPropertyDataTypeEnum.DATA_TYPE_DOUBLE ? +c.value : 0,
            valueString: c.dataType === EntityPropertyDataTypeEnum.DATA_TYPE_STRING ? c.value : '',
            userCode: 'API',
            changeDate: new Date().toISOString(),
            dataType: c.dataType,
          };
        }),
      entityRelations: formValue.entityRelations.map(erc => {
        return <IEntityRelation>{
          sortOrder: erc.sortOrder,
          productIndex: erc.entityIndex,
          activityIndex: this._entity.index,
          relation: this.poService.validateQuantityAsPerDirection(Number(erc.relation), Number(erc.direction)),
          direction: erc.direction,
          unitCode: erc.unitCode,
          userCode: erc.userCode,
          ChangeDate: new Date().toISOString(),
          productCode: erc.productCode,
          activityCode: erc.activityCode,
          isNew: erc.isNew,
          isChange: true,
          isRemoved: erc.isRemoved,
          inTactical: erc.inTactical,
          inOperative: erc.inOperative,
          isChaining: erc.isChaining,
          batchTemplateId: erc.batchTemplateId,
          isPreferred: erc.isPreferred,
          glulamSpec: this.glulamSpecs()
            ?.value.filter(f => f.productIndex === erc.entityIndex)
            .map(spec => ({ ...spec, isChanged: true })),
        };
      }),
      unitConversions: this.unitConversions.controls.map<IUnitConversion>(c => {
        return <IUnitConversion>{
          index: c.value.index ?? 0,
          entityIndex: this._entity.index ? this._entity.index : 0,
          unitCode: c.value.unitCode,
          convFactor: c.value.convFactor,
          userCode: 'API',
          changeDate: new Date().toISOString(),
          isNew: this.isNew,
          isChange: true,
          isRemoved: c.value.isRemoved,
          id: c.value.id ?? 0,
          mandatoryUnitRelation: c.value.mandatoryUnitRelation,
        };
      }),
      autoGenerateProdOrder: false,
    };

    this.disableSubmit = true;
    if (e.index === -1) {
      this.entityService
        .insert(e)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.disableSubmit = false;
            this.dialogRef.close(true);
          },
          error: () => {
            this.disableSubmit = false;
          },
        });
    } else {
      this.entityService
        .update(e)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.disableSubmit = false;
            this.dialogRef.close(true);
          },
          error: () => {
            this.disableSubmit = false;
          },
        });
    }
  }

  get unitConversions(): FormArray<FormGroup<CreateEntityUnitConversionForm>> {
    return this.entityForm.controls.unitConversions;
  }

  get propertyValues(): FormArray<FormGroup<CreatePropertyValueEntityForm>> {
    return this.entityForm.controls.propertyValues;
  }

  openAddEditGlulamSpecModal() {
    const dr = this.entityService.openDialogForGlulamSpec(this.data.entity, this.glulamSpecForm);
    dr.result.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((d: any) => {
      if (d instanceof DialogCloseResult) {
        dr.close();
      } else if (d.success) {
        this.glulamSpecForm = d.glulamspec;
      }
    });
  }

  isRelationInvalid(relationGroup: AbstractControl): boolean {
    if (!relationGroup) {
      return false;
    }

    const relationControl = relationGroup.get('relation');
    if (!relationControl) {
      return false;
    }

    return (
      (relationControl.hasError('hourRequiresNegative')
        || relationControl.hasError('relationCannotBeZero')
        || relationControl.hasError('consumesRequiresNegative')
        || relationControl.hasError('producesRequiresPositive')) &&
      (relationControl.touched || relationControl.dirty || relationControl.invalid)
    );
  }

  isDirectionInvalid(relationGroup: AbstractControl): boolean {
    if (!relationGroup) {
      return false;
    }

    const directionControl = relationGroup.get('direction');
    if (!directionControl) {
      return false;
    }

    return (directionControl.hasError('consumesRequiresNegative') || directionControl.hasError('producesRequiresPositive'))
      && (directionControl.touched || directionControl.dirty || directionControl.invalid);
  }

  getRelationErrorKey(relationGroup: AbstractControl): string | null {
    const relationControl = relationGroup.get('relation');
    if (!relationControl || !(relationControl.touched || relationControl.dirty)) {
      return null;
    }

    if (relationControl.hasError('relationCannotBeZero')) {
      return 'Validation.RelationCannotBeZero';
    }

    if (relationControl.hasError('hourRequiresNegative')) {
      return 'Validation.HourRequiresNegative';
    }

    if (relationControl.hasError('consumesRequiresNegative')) {
      return 'Validation.ConsumesRequiresNegative';
    }

    if (relationControl.hasError('producesRequiresPositive')) {
      return 'Validation.ProducesRequiresPositive';
    }

    return null;
  }

  getDirectionErrorKey(relationGroup: AbstractControl): string | null {
    const directionControl = relationGroup.get('direction');
    if (!directionControl || !(directionControl.touched || directionControl.dirty)) {
      return null;
    }

    if (directionControl.hasError('consumesRequiresNegative')) {
      return 'Validation.ConsumesRequiresNegative';
    }

    if (directionControl.hasError('producesRequiresPositive')) {
      return 'Validation.ProducesRequiresPositive';
    }

    return null;
  }

  isMandatoryPropertyInvalid(propertyControl: AbstractControl): boolean {
    const valueControl = propertyControl.get('value');
    if (!valueControl) {
      return false;
    }

    const isMandatoryControl = propertyControl.value.isMandatory === true;

    if (isMandatoryControl && !valueControl.hasValidator(Validators.required)) {
      valueControl.addValidators(Validators.required);
      valueControl.updateValueAndValidity({ emitEvent: false });
    }

    if (!isMandatoryControl && valueControl.hasValidator(Validators.required)) {
      valueControl.removeValidators(Validators.required);
      valueControl.updateValueAndValidity({ emitEvent: false });
      return false;
    }

    return isMandatoryControl && valueControl.invalid && (valueControl.touched || valueControl.dirty);
  }

  confirmEntityRelationRemovalMsg(): string {
    return this.translateService.instant('EntityTranslation.ConfirmEntityRelationRemoval');
  }

  getStringValue = (pv: IPropertyValue): string => {
    if (pv.dataType === EntityPropertyDataTypeEnum.DATA_TYPE_INT) {
      return pv?.valueInt?.toString() ?? '';
    } else if (pv.dataType === EntityPropertyDataTypeEnum.DATA_TYPE_DOUBLE) {
      return pv?.valueDouble?.toString() ?? '';
    } else {
      return pv?.valueString ?? '';
    }
  };
  validateRelationByUnitCode(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value || control.value === '') {
        return { relationCannotBeZero: true };
      }

      const relationValue = Number(control.value);
      if (Number.isNaN(relationValue)) {
        return null;
      }
      if (relationValue === 0) {
        return { relationCannotBeZero: true };
      }

      const unitCode = (control.parent?.get('unitCode')?.value ?? '').toString().trim().toUpperCase();
      if (unitCode === 'HOUR' && relationValue >= 0) {
        return { hourRequiresNegative: true };
      }

      const direction = control.parent?.get('direction')?.value;
      if (direction === EntityRelationDirection.CONSUMES && relationValue > 0) {
        return { consumesRequiresNegative: true };
      }

      if (direction === EntityRelationDirection.PRODUCE && relationValue < 0) {
        return { producesRequiresPositive: true };
      }

      // Else: user has updated (corrected) the relation, reset form-validation.
      this.removeErrors(control);
      return null;
    };
  }

  validateDirectionForNegativeRelation(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const relationRaw = control.parent?.get('relation')?.value;

      if (!relationRaw || relationRaw === '') {
        return null;
      }

      const relationValue = Number(relationRaw);
      const direction = control.parent?.get('direction')?.value;

      if (direction === EntityRelationDirection.CONSUMES && relationValue > 0) {
        return { consumesRequiresNegative: true };
      }

      if (direction === EntityRelationDirection.PRODUCE && relationValue < 0) {
        return { producesRequiresPositive: true };
      }

      this.removeErrors(control);
      return null;
    };
  }

  removeErrors(control: AbstractControl<any, any, any>) {
    const currentErrors = control.errors ? { ...control.errors } : null;
    if (currentErrors) {
      delete currentErrors['consumesRequiresNegative'];
      delete currentErrors['producesRequiresPositive'];
      control.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
    }
  }

  onRowReorder(event: any): void {
    const dragged = event.draggedRows[0];
    const target = event.dropTargetRow;

    if (!dragged || !target) {
      return;
    }

    const fromIndex = dragged.dataItem.formIndex;
    const toIndex = target.dataItem.formIndex;

    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) {
      return;
    }

    const movedControl = this.entityRelations.at(fromIndex);
    if (!movedControl) {
      return;
    }

    this.entityRelations.removeAt(fromIndex);
    this.entityRelations.insert(toIndex, movedControl);
    this.recalculateEntityRelationSortOrder();
  }

  private recalculateEntityRelationSortOrder(): void {
    let sortOrder = 1;
    this.entityRelations.controls.forEach(relationGroup => {
      if (relationGroup.value?.isRemoved) {
        return;
      }

      relationGroup.get('sortOrder')?.patchValue(sortOrder++, { emitEvent: false });
    });
  }

  private getNextRelationSortOrder(): number {
    return this.entityRelations.controls.filter(c => !c.value?.isRemoved).length + 1;
  }

  private readonly batchTemplateState: State = { skip: 0, take: 100, filter: { logic: 'and', filters: [] } };

  private loadBatchTemplates(): void {
    const machineId = this._entity.machineId;
    const machineGroupId = this._entityGroup.machineGroupId;

    if (machineId) {
      this.batchTemplateService
        .getbatchTemplatesByMachineIds(this.batchTemplateState, [machineId])
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(templates => {
          this.batchTemplateOptions = groupBy(templates, [{ field: 'machineName' }]) as GroupResult[];
        });
    } else if (machineGroupId) {
      this.machineGroupService
        .queryv2({ machineGroupId })
        .pipe(
          switchMap(result => {
            const machineIds = result.data?.[0]?.machineIds ?? [];
            return machineIds.length > 0
              ? this.batchTemplateService.getbatchTemplatesByMachineIds(this.batchTemplateState, machineIds)
              : of([]);
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(templates => {
          this.batchTemplateOptions = groupBy(templates, [{ field: 'machineName' }]) as GroupResult[];
        });
    }
  }
}
